import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  Divider,
  Button,
} from '@mui/material';
import {
  Visibility,
  FilterList,
  Print,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { studentAPI, branchAPI, batchAPI, courseAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const AlumniList = () => {
  const { user, isSuperAdmin } = useAuth();

  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    centre: '',
    search: '',
    batch: '',
    course: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    fetchAlumni();
    if (isSuperAdmin) {
      fetchBranches();
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchBatches();
    fetchCourses();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll({ limit: 1000 });
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data.data?.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchAlumni = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await studentAPI.getAllAlumni(params);
      setAlumni(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching alumni:', error);
      toast.error('Failed to load alumni');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = async (alumniId) => {
    try {
      const response = await studentAPI.getOne(alumniId);
      setSelectedAlumni(response.data.data.student);
      setDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching alumni details:', error);
      toast.error('Failed to load alumni details');
    }
  };

  const handlePrint = (alumniData) => {
    sessionStorage.setItem('printStudent', JSON.stringify(alumniData));
    const printWindow = window.open('/admin/students/print', '_blank');
    if (printWindow) {
      printWindow.focus();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Alumni
        </Typography>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Centre</InputLabel>
                <Select
                  name="centre"
                  value={filters.centre}
                  onChange={handleFilterChange}
                  label="Centre"
                  MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                >
                  <MenuItem value="">All Centres</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name} ({branch.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Name or Form Number"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Batch</InputLabel>
              <Select
                name="batch"
                value={filters.batch}
                onChange={handleFilterChange}
                label="Batch"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Batches</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name} ({b.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Course</InputLabel>
              <Select
                name="course"
                value={filters.course}
                onChange={handleFilterChange}
                label="Course"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Courses</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name} ({c.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              name="startDate"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              name="endDate"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Form No</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Course</strong></TableCell>
                <TableCell><strong>Centre</strong></TableCell>
                <TableCell><strong>Converted Date</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : alumni.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No alumni found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                alumni.map((alumniData) => (
                  <TableRow
                    key={alumniData._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewDetails(alumniData._id)}
                  >
                    <TableCell>{alumniData.formNumber || '-'}</TableCell>
                    <TableCell>{alumniData.personalInfo?.nameAsPerTC || '-'}</TableCell>
                    <TableCell>
                      {alumniData.course?.name || '-'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {alumniData.course?.code || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {alumniData.centre?.name || '-'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {alumniData.centre?.code || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(alumniData.convertedToAlumniAt)}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(alumniData._id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Print">
                        <IconButton
                          size="small"
                          onClick={() => handlePrint(alumniData)}
                        >
                          <Print />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedAlumni && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Alumni Details
                </Typography>
                <Chip
                  label="ALUMNI"
                  color="info"
                  size="small"
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {/* Photo */}
                {selectedAlumni.photo && (
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/${selectedAlumni.photo}`}
                        alt="Alumni Photo"
                      />
                    </Card>
                  </Grid>
                )}

                {/* Basic Info */}
                <Grid item xs={12} md={selectedAlumni.photo ? 9 : 12}>
                  <Typography variant="subtitle2" color="text.secondary">Form Number</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedAlumni.formNumber}</Typography>

                  <Typography variant="subtitle2" color="text.secondary">Course</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedAlumni.course?.name} ({selectedAlumni.course?.code})
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">Centre</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedAlumni.centre?.name} ({selectedAlumni.centre?.code})
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">Converted to Alumni On</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatDate(selectedAlumni.convertedToAlumniAt)}
                  </Typography>
                </Grid>

                {/* Personal Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Personal Information</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name (as per TC)</Typography>
                  <Typography variant="body1">{selectedAlumni.personalInfo?.nameAsPerTC}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body1">{formatDate(selectedAlumni.personalInfo?.dateOfBirth)}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                  <Typography variant="body1">{selectedAlumni.personalInfo?.gender}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedAlumni.personalInfo?.phone}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedAlumni.personalInfo?.email || '-'}</Typography>
                </Grid>

                {/* Addresses */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Addresses</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Permanent Address</Typography>
                  <Typography variant="body2">
                    {selectedAlumni.permanentAddress?.street}<br />
                    {selectedAlumni.permanentAddress?.city}, {selectedAlumni.permanentAddress?.district}<br />
                    {selectedAlumni.permanentAddress?.state} - {selectedAlumni.permanentAddress?.pincode}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Communication Address</Typography>
                  <Typography variant="body2">
                    {selectedAlumni.communicationAddress?.street}<br />
                    {selectedAlumni.communicationAddress?.city}, {selectedAlumni.communicationAddress?.district}<br />
                    {selectedAlumni.communicationAddress?.state} - {selectedAlumni.communicationAddress?.pincode}
                  </Typography>
                </Grid>

                {/* Educational Qualifications */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Educational Qualifications</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Examination</TableCell>
                          <TableCell>University/Board</TableCell>
                          <TableCell>Year</TableCell>
                          <TableCell>Class/Division</TableCell>
                          <TableCell>Grade/%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedAlumni.educationalQualifications?.map((qual, index) => (
                          <TableRow key={index}>
                            <TableCell>{qual.examination}</TableCell>
                            <TableCell>{qual.universityOrBoard}</TableCell>
                            <TableCell>{qual.yearOfPassing}</TableCell>
                            <TableCell>{qual.classOrDivision}</TableCell>
                            <TableCell>{qual.gradeOrPercentage}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AlumniList;
